interface SelectOptions {
  select: HTMLSelectElement;
  renderSelected?: (count: number) => string;
}

interface SelectElements {
  container: HTMLElement;
  label: HTMLElement;
  selectedValue: HTMLElement;
  list: HTMLElement;
  listItems: HTMLElement[];
}

export function Select({ select, renderSelected }: SelectOptions) {
  const options = Array.from(select.querySelectorAll('option'));
  const { container, selectedValue, label, list, listItems } = buildSelect();

  render();

  function render() {
    select.style.display = 'none';
    select.parentNode?.append(container);

    setSelectedText();

    label.addEventListener('click', handleListToggle);
    listItems.forEach((option) => option.addEventListener('click', handleListItemSelection));
  }

  function handleClickOutside({ target }: Event) {
    const hasClickedOutside = !list.contains(target as HTMLElement);

    if (hasClickedOutside) {
      closeList();
    }
  }

  function handleListToggle(event: MouseEvent) {
    event.preventDefault();
    if (!list.classList.contains('open')) {
      openList();
    } else {
      closeList();
    }
  }

  function handleListItemSelection(event: MouseEvent) {
    event.preventDefault();
    const option = event.target as HTMLOptionElement;
    setOption(option);
    setSelectedText();
  }

  function openList() {
    if (!list.classList.contains('open')) {
      list.classList.add('open');
      setTimeout(() => document.addEventListener('click', handleClickOutside), 0);
    }
  }

  function closeList() {
    list.classList.remove('open');
    document.removeEventListener('click', handleClickOutside);
  }

  function setOption(option: HTMLElement) {
    const selected = option.classList.contains('selected');
    const index = listItems.indexOf(option);

    if (index === -1) {
      return;
    }

    if (selected) {
      options[index].removeAttribute('selected');
    } else {
      options[index].setAttribute('selected', 'selected');
    }

    option.classList.toggle('selected');
  }

  function setSelectedText() {
    const count = options.filter(({ selected }) => selected).length;

    selectedValue.innerText = renderSelected?.(count) ?? `${count} items`;
  }

  function buildSelect(): SelectElements {
    const containerElement = createElement('div', CLASSNAMES.container);
    const [selectedElement, selectedValueElement] = buildSelected();
    const [dropDownElement, dropDownItemElements] = buildDropdown();

    containerElement.append(selectedElement, dropDownElement);

    return {
      container: containerElement,
      label: selectedElement,
      selectedValue: selectedValueElement,
      list: dropDownElement,
      listItems: dropDownItemElements,
    };
  }

  function buildSelected(): [HTMLElement, HTMLElement, HTMLElement] {
    const element = createElement('div', CLASSNAMES.selected);
    const label = createElement('strong');
    const value = createElement('span');

    const placeholderText = select.getAttribute('placeholder');

    if (placeholderText) {
      label.innerText = `${placeholderText}:`;
    }

    element.append(label, value);

    return [element, value, label];
  }

  function buildOptions() {
    const optionElements = options.map((option) => {
      const element = createElement('li', CLASSNAMES.dropdownItem);
      element.innerHTML = option.innerHTML;

      if (option.selected) {
        element.classList.add('selected');
      }

      return element;
    });

    return optionElements;
  }

  function buildDropdown(): [HTMLElement, HTMLElement[]] {
    const element = createElement('ul', CLASSNAMES.dropdown);
    const optionElements = buildOptions();
    element.append(...optionElements);

    return [element, optionElements];
  }

  function createElement(tag: string, className?: string) {
    const element = document.createElement(tag);
    if (className) {
      element.classList.add(className);
    }
    return element;
  }
}

const CLASSNAMES = {
  container: 'c-select',
  selected: 'c-select__selected',
  dropdown: 'c-select__dropdown',
  dropdownItem: 'c-select__dropdown__item',
};
